import { useContext, useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled, Box, BrandLogo, Button } from '@ltvco/refresh-lib/theme';
import { AppConfig, usePermissions } from '@ltvco/refresh-lib/ctx';
import {
  HeaderContainer,
  StyledContainer,
  StyledHeader,
  StyledHeaderNav,
  StyledBannerContainer,
} from './components';
import { ClassicHeaderContents } from './ClassicHeaderContents/ClassicHeaderContents';
import {
  BannerMigrationCta,
  FreemiumUpgradeBanner,
} from '@ltvco/refresh-lib/v2';
import { GoodhoodPromoBanner } from 'components/GoodhoodPromo';
import { useIpAddress } from '@ltvco/refresh-lib/hooks';
import { useIpToZip } from 'hooks/useIpToZip';
import { DallasZips } from 'components/GoodhoodPromo/constants';
import { useScreenSize } from '@ltvco/refresh-lib/utils';

const ButtonContainer = styled(Box)(({ theme }) => ({
  width: '55px',
  display: 'flex',
  flex: 1,

  button: {
    minWidth: 'inherit',
    padding: 0,
    height: '100%',
    width: '55px',

    '&:hover': {
      background: 'inherit',
    },
  },

  [theme.breakpoints.up('sm')]: {
    width: 'auto',
    paddingLeft: '1rem',

    button: {
      width: 'inherit',
    },
  },
}));

const StyledButton = styled(Button)({
  height: 'fit-content',
  py: 1.875,
  backgroundColor: 'transparent',

  '&.MuiButtonBase-root:hover': {
    bgcolor: 'transparent',
  },
});

interface ClassicHeaderProps {
  /** Callback to handle logout */
  handleLogout: () => void;
}

export const ClassicHeader = ({ handleLogout }: ClassicHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef(null);
  const { trackEventGA4 } = useContext(AppConfig);
  const { isFreePlan } = usePermissions();
  const { isMobile } = useScreenSize();

  const [isGoodhoodBannerVisible, setIsGoodhoodBannerVisible] = useState(() => {
    const saved = localStorage.getItem('goodhoodBannerVisisble');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [isFreemiumBannerVisible, setIsFreemiumBannerVisible] = useState(
    isFreePlan ?? false
  );

  const isInSelfInspectionRoute = location?.pathname.includes(
    '/vehicle-self-inspection'
  );

  const ip = useIpAddress();
  const { data: cityStateLocationData } = useIpToZip(ip);
  const zipIsADallasZip = cityStateLocationData
    ? DallasZips.includes(cityStateLocationData?.entities?.locations[0]?.zip5)
    : false;

  const isOnLoginPage = location.pathname.includes('/login');
  const shouldRenderGoodhood =
    isGoodhoodBannerVisible && zipIsADallasZip && !isOnLoginPage;

  useEffect(() => {
    // Ensure freemium banner reflects isFreePlan once it resolves
    if (typeof isFreePlan === 'boolean') {
      setIsFreemiumBannerVisible(isFreePlan);
    }
  }, [isFreePlan]);

  useEffect(() => {
    let headerHeight = 0;
    const appContainer = document.getElementById('app-container');
    const mainHeaderContainer = document.getElementById('main-header');
    const outerHeaderContainer = document.getElementById(
      'outer-header-container'
    );
    const ssoPromoBanner = document.getElementById('sso-promo-banner');

    if (ssoPromoBanner) {
      headerHeight += ssoPromoBanner.clientHeight;
    }

    if (mainHeaderContainer) {
      headerHeight += mainHeaderContainer.clientHeight;
    }

    if (shouldRenderGoodhood) {
      headerHeight += 48;
    }

    const freemiumBanner = document.getElementById('freemium-banner');
    if (freemiumBanner) {
      headerHeight += freemiumBanner.clientHeight;
    }

    if (outerHeaderContainer) {
      outerHeaderContainer.style.height = `${headerHeight}px`;
    }

    if (appContainer) {
      const marginTop = 'auto';

      appContainer.style.marginTop = marginTop;
    }
  }, [shouldRenderGoodhood, isFreePlan, isFreemiumBannerVisible]);

  useEffect(() => {
    localStorage.setItem(
      'goodhoodBannerVisisble',
      JSON.stringify(isGoodhoodBannerVisible)
    );
  }, [isGoodhoodBannerVisible]);

  const handleBrandLogoClick = () => {
    trackEventGA4({
      event_name: 'header_engagement',
      type: 'nav - brand logo',
    });
    if (isInSelfInspectionRoute) return null;

    navigate('/dashboard');
  };

  const handleFreemiumBannerClose = () => {
    setIsFreemiumBannerVisible(false);
  };

  const handleFreemiumBannerUpgrade = () => {
    navigate('/subscribe');
  };

  return (
    <HeaderContainer ref={headerRef} id="outer-header-container">
      <StyledContainer id="styled-header">
        <StyledBannerContainer>
          <BannerMigrationCta
            hasCustomMargin={zipIsADallasZip}
            hasCustomStyle={!zipIsADallasZip}
          />
          <GoodhoodPromoBanner
            isVisible={shouldRenderGoodhood}
            setIsVisible={setIsGoodhoodBannerVisible}
          />
        </StyledBannerContainer>
        <StyledHeader
          direction={'row'}
          justifyContent={'space-between'}
          id="main-header"
        >
          <StyledHeaderNav>
            <ButtonContainer>
              <StyledButton
                disabled={isInSelfInspectionRoute}
                className="brand-logo-btn"
                disableRipple
                onClick={handleBrandLogoClick}
                data-testid="brand-logo-btn"
              >
                <BrandLogo />
              </StyledButton>
            </ButtonContainer>
            {!isInSelfInspectionRoute && (
              <ClassicHeaderContents handleLogout={handleLogout} />
            )}
          </StyledHeaderNav>
        </StyledHeader>
        {isFreePlan && isFreemiumBannerVisible && (
          <StyledBannerContainer id="freemium-banner">
            <FreemiumUpgradeBanner
              isFreePlan={isFreePlan}
              isOpen={isFreemiumBannerVisible}
              onClose={handleFreemiumBannerClose}
              isMobile={isMobile}
              onUpgradeClick={handleFreemiumBannerUpgrade}
            />
          </StyledBannerContainer>
        )}
      </StyledContainer>
    </HeaderContainer>
  );
};
